import React, { useMemo, useState } from 'react';
import { Box, Divider, Text, useColorModeValue } from '@chakra-ui/react';
import Highcharts, { Options } from 'highcharts';
import Chart from 'highcharts-react-official';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ArcButton } from '../../../common/components/ArcButton';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT } from '../../../common/constants';
import { ConfirmTariffPageBase } from '../ConfirmTariffPageBase';
import { SeasonMultiToggleSwitch } from '../SeasonMultiToggleSwitch';
import { useTariffChartTooltipSizes } from '../tariff-constants';
import { adjustSeasonDate, formatDollars } from '../tariff-helpers';
import { useHandleSubmitForm } from '../useHandleSubmitForm';
import { useTariffFormContext } from '../useTariffFormContext';

export const ConfirmFlatTariff = () => {
  const { control, isDirtyForm } = useTariffFormContext();
  const tariff = useWatch({ control, name: `tariff` });
  const tariffId = useWatch({ control, name: 'id' });
  // A new tariff (no id) is always created on submit (see useHandleSubmitForm), so offer Confirm
  // Rates even when the form matches its defaults — e.g. a WattBuy import.
  const hasChangesToSave = isDirtyForm || !tariffId;
  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(0);
  const { handleSubmitForm, isSubmitting } = useHandleSubmitForm();
  const { t, i18n } = useTranslation();

  const chartOptions = useChartOptions(selectedSeasonIndex);

  const { formattedStartDate, formattedEndDate } = useMemo(() => {
    const startDate = adjustSeasonDate(
      tariff?.seasons[selectedSeasonIndex]?.fromMonth,
      tariff?.seasons[selectedSeasonIndex]?.fromDate
    );

    const endDate = adjustSeasonDate(
      tariff?.seasons[selectedSeasonIndex]?.toMonth,
      tariff?.seasons[selectedSeasonIndex]?.toDate
    );

    const formattedStartDate = startDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    const formattedEndDate = endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });

    return { formattedStartDate, formattedEndDate };
  }, [i18n.language, selectedSeasonIndex, tariff?.seasons]);

  return (
    <ConfirmTariffPageBase>
      <Box h={`calc(${MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT} - ${hasChangesToSave ? '80px' : '20px'})`} overflow={'auto'}>
        <SEHomeCard borderTopLeftRadius={'5px'} borderBottomRightRadius={'39px'} pr={0} pb={4}>
          <Text fontSize={20}>{tariff?.planName}</Text>
          <Divider borderColor={'#9797974D'} my={2} />

          <Text fontSize={17} fontWeight={500}>
            {t('Energy Rates.flat plan').toUpperCase()}
          </Text>

          <Box
            color={'#111111'}
            _dark={{
              color: '#CBCBCB',
            }}
          >
            <Text mt={4}>{t('Energy Rates.import rate')}</Text>
            <Text>
              {t('Common.cost')}: ${tariff?.rates?.[selectedSeasonIndex]?.import?.[0]?.rateBands[0].rate} / kWh
            </Text>
          </Box>
        </SEHomeCard>

        <SEHomeCard borderTopLeftRadius={'5px'} borderBottomRightRadius={'39px'} my={4}>
          <SeasonMultiToggleSwitch
            value={selectedSeasonIndex}
            onChange={(newIndex) => setSelectedSeasonIndex(newIndex)}
          />
          <Text my={4}>
            {formattedStartDate} - {formattedEndDate}
          </Text>

          <Box>
            <Chart highcharts={Highcharts} options={chartOptions} />
          </Box>
        </SEHomeCard>
      </Box>
      <ArcButton
        arcColor="#3DCD58"
        w="100%"
        py={6}
        isLoading={isSubmitting}
        data-testid="primary-button"
        onClick={handleSubmitForm}
        isFixedAtScreenBottom={true}
      >
        {hasChangesToSave ? t('Energy Rates.confirm rates') : t('Energy Rates.back to tariff list')}
      </ArcButton>
    </ConfirmTariffPageBase>
  );
};

function useChartOptions(selectedSeasonIndex: number) {
  const { control } = useTariffFormContext();
  const tariff = useWatch({ control, name: `tariff` });
  const textColor = useColorModeValue('black', 'white');
  const { t } = useTranslation();
  const {
    fontSize: tooltipFontSize,
    markerSize: tooltipMarkerSize,
    padding: tooltipPadding,
    borderRadius: tooltipBorderRadius,
  } = useTariffChartTooltipSizes();

  return useMemo(() => {
    const importRate = Number(tariff?.rates?.[selectedSeasonIndex]?.import?.[0]?.rateBands[0].rate) || 0;

    const chartOptions: Options = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 210,
        animation: false,
        marginBottom: 100,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: [''],
        labels: { useHTML: true },
        crosshair: true,
        lineColor: 'transparent',
        accessibility: { enabled: true, description: t('Energy Rates.rates') },
      },
      yAxis: {
        title: {
          text: `${t('Energy Rates.rate')} ( $ / kWh )`,
          style: { color: textColor },
        },
        tickAmount: 6,
        labels: {
          style: { color: textColor, fontSize: '12px' },
          formatter: function () {
            const value = Number(this.value);
            if (!value) return '0';
            return formatDollars(value);
          },
          align: 'right',
          x: -2,
        },
      },
      tooltip: {
        useHTML: true,
        animation: false,
        followPointer: true,
        valueSuffix: '¢',
        style: {
          color: 'white',
          fontSize: tooltipFontSize,
          lineHeight: '1.2',
          whiteSpace: 'normal',
        },
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: tooltipBorderRadius,
        borderWidth: 0,
        shadow: false,
        padding: tooltipPadding,
        formatter: function () {
          return `
            <div style="display:flex; flex-direction:column; gap:2px; white-space:nowrap; min-width:0;">
              <div style="display:flex; align-items:center; gap:4px; min-width:0;">
                <span style="color:${
                  this.color
                }; font-size:${tooltipMarkerSize}px; line-height:1; display:inline-block;">∎</span>
                <span style="display:inline-block; min-width:0;">${this.series.name}</span>
              </div>
              <div style="display:block; min-width:0;">${formatDollars(Number(this.y) || 0)} ${t(
            'Energy Rates.per'
          )} kWh</div>
            </div>
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 0,
        },
      },
      legend: {
        align: 'left',
        itemStyle: { textOverflow: 'auto' },
        itemMarginTop: 1,
        labelFormatter: function () {
          // @ts-expect-error - not in types but accessible
          const yValue = this.yData[0];
          const style = `color: ${textColor}; font-size: 16px; fontWeight: 400;`;
          return `<span style="${style}">${this.name}: ${formatDollars(yValue)} ${t('Energy Rates.per')} kWh</span>`;
        },
        symbolRadius: 0,
        padding: 0,
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: t('Energy Rates.import rate').toUpperCase(),
          type: 'column',
          color: '#3DCD58',
          data: [
            {
              borderColor: '#3DCD58',
              y: importRate,
              marker: { symbol: 'square' },
            },
          ],
        },
      ],
    };

    return chartOptions;
  }, [
    selectedSeasonIndex,
    t,
    tariff?.rates,
    textColor,
    tooltipBorderRadius,
    tooltipFontSize,
    tooltipMarkerSize,
    tooltipPadding,
  ]);
}
